import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Filter, X, SearchX } from 'lucide-react';
import ProductCard from '../../components/molecules/ProductCard/ProductCard';
import { useCategories } from '../../hooks/useCategories';
import { useProducts } from '../../hooks/useProducts';
import { useBrands } from '../../hooks/useBrands';
import { useBranches } from '../../hooks/useBranches';
import styles from './CollectionsPage.module.css';

const CollectionsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const categoryId  = searchParams.get('category') || 'all';
  const brandSlug   = searchParams.get('brand')    || 'all';
  const brandName   = searchParams.get('brandName') || '';
  const branchId    = searchParams.get('branch')   || 'all';
  const branchName  = searchParams.get('branchName') || '';

  const { categories, loading: categoriesLoading } = useCategories();
  const { brands,     loading: brandsLoading }     = useBrands();
  const { branches,   loading: branchesLoading }   = useBranches();

  // Build product query params
  const productParams = {};
  if (categoryId !== 'all') productParams.category = categoryId;
  if (brandSlug  !== 'all') productParams.brand    = brandSlug;
  if (branchId   !== 'all') productParams.branch   = branchId;

  const { products, loading: productsLoading } = useProducts(productParams);

  // ── Filter handlers ──────────────────────────────────────────
  const handleCategoryFilter = (id) => {
    const next = {};
    if (id !== 'all') next.category = id;
    if (brandSlug !== 'all') { next.brand = brandSlug; next.brandName = brandName; }
    if (branchId !== 'all') { next.branch = branchId; next.branchName = branchName; }
    setSearchParams(next);
  };

  const handleBrandFilter = (slug, name) => {
    const next = {};
    if (slug !== 'all') { next.brand = slug; next.brandName = name; }
    if (categoryId !== 'all') next.category = categoryId;
    if (branchId !== 'all') { next.branch = branchId; next.branchName = branchName; }
    setSearchParams(next);
  };

  const handleBranchFilter = (id, city) => {
    const next = {};
    if (id !== 'all') { next.branch = id; next.branchName = city; }
    if (categoryId !== 'all') next.category = categoryId;
    if (brandSlug !== 'all') { next.brand = brandSlug; next.brandName = brandName; }
    setSearchParams(next);
  };

  // ── Display labels ───────────────────────────────────────────
  // ── Display labels ───────────────────────────────────────────
  const currentCategoryName = categoryId === 'all'
    ? 'All'
    : categories.find(c => c.id === categoryId)?.name || 'Collection';

  let activeTitle = '';
  if (brandSlug !== 'all') activeTitle += brandName || brandSlug;
  if (branchId !== 'all') activeTitle += (activeTitle ? ' & ' : '') + (branchName || 'Branch');
  if (!activeTitle) activeTitle = 'All Brands';

  const pageSubtitle = (brandSlug !== 'all' || branchId !== 'all')
    ? `Showing products for ${activeTitle}${categoryId !== 'all' ? ` in ${currentCategoryName}` : ''}`
    : `Browse our premium wholesale ${currentCategoryName.toLowerCase()} collections`;

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <div className="container">
          <h1 className="heading-xl">Our Collections</h1>
          <p className="text-body text-dim">{pageSubtitle}</p>
        </div>
      </header>

      <section className={styles.content}>
        <div className="container">
          
          <button 
            className={styles.sidebarToggle}
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          >
            {isSidebarOpen ? <X size={20} /> : <Filter size={20} />}
            <span>{isSidebarOpen ? 'Hide Filters' : 'Show Filters'}</span>
          </button>

          <div className={styles.layoutWrapper}>
            {/* ── Sidebar Filters ── */}
            <aside className={`${styles.sidebar} ${isSidebarOpen ? styles.isOpen : ''}`}>

          {/* ── Category Filters ── */}
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Category</span>
            <div className={styles.filters}>
              <button
                className={`${styles.filterBtn} ${categoryId === 'all' ? styles.active : ''}`}
                onClick={() => handleCategoryFilter('all')}
              >
                All
              </button>
              {!categoriesLoading && categories.map((cat) => (
                <button
                  key={cat.id}
                  className={`${styles.filterBtn} ${categoryId === cat.id ? styles.active : ''}`}
                  onClick={() => handleCategoryFilter(cat.id)}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

            {/* ── Brand Filters ── */}
            {!brandsLoading && brands.length > 0 && (
              <div className={styles.filterGroup}>
                <span className={styles.filterLabel}>Brand</span>
                <div className={styles.filters}>
                  <button
                    className={`${styles.filterBtn} ${brandSlug === 'all' ? styles.active : ''}`}
                    onClick={() => handleBrandFilter('all', '')}
                  >
                    All Brands
                  </button>
                  {brands.map((brand) => (
                    <button
                      key={brand.id}
                      className={`${styles.filterBtn} ${brandSlug === brand.slug ? styles.activeBrand : ''}`}
                      onClick={() => handleBrandFilter(brand.slug, brand.name)}
                    >
                      {brand.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Branch Filters ── */}
            {!branchesLoading && branches.length > 0 && (
              <div className={styles.filterGroup}>
                <span className={styles.filterLabel}>Branch</span>
                <div className={styles.filters}>
                  <button
                    className={`${styles.filterBtn} ${branchId === 'all' ? styles.active : ''}`}
                    onClick={() => handleBranchFilter('all', '')}
                  >
                    All Branches
                  </button>
                  {branches.map((branch) => (
                    <button
                      key={branch.id}
                      className={`${styles.filterBtn} ${branchId === branch.id ? styles.active : ''}`}
                      onClick={() => handleBranchFilter(branch.id, branch.city)}
                    >
                      {branch.city}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </aside>

          {/* ── Products Grid ── */}
          <div className={styles.productsArea}>
            {productsLoading ? (
              <div className={styles.loaderWrapper}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B1A2F]"></div>
              </div>
            ) : (
              <>
              <div className={styles.grid}>
                {products.map((product) => (
                  <div key={product.id} className={styles.cardWrapper}>
                    <Link
                      to={`/enquiry?product=${encodeURIComponent(product.collection)}&sku=${product.sku}`}
                      className={styles.linkOverlay}
                      aria-label={`Enquiry for ${product.collection}`}
                    />
                    <ProductCard {...product} />
                  </div>
                ))}
              </div>

                {products.length === 0 && (
                  <div className={styles.noResults}>
                    <div className={styles.noResultsIconWrapper}>
                      <SearchX size={48} className={styles.noResultsIcon} />
                    </div>
                    <p className={styles.noResultsText}>
                      {(brandSlug !== 'all' || branchId !== 'all')
                        ? <>No products found for <strong>{activeTitle}</strong>.</>
                        : <>No products found in this category.</>}
                    </p>
                    <p className={styles.noResultsSub}>
                      Try adjusting your filters to discover more items.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CollectionsPage;
